import { useEffect, useRef, useState } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
    IconLoader2, IconRotateClockwise, IconRotate2, IconRestore, IconX,
} from '@tabler/icons-react';
import { T } from './figma';
import { loadImage } from '../utils/thumbnailUpload';

/**
 * Crop + aspect ratio for the gallery thumbnail.
 *
 * The dialog hands back a PERCENT crop rect plus a quarter-turn rotation; the
 * caller cuts the pixels (cropImageToThumbnail) from the untouched original, so
 * the same choice can be re-cut later without stacking up JPEG losses. Nothing
 * here touches the template document.
 *
 * Rotation is baked into a preview image rather than applied as a CSS transform:
 * ReactCrop measures the element's layout box, and a rotated box no longer lines
 * up with the pixels underneath it.
 *
 * Plain fixed overlay rather than a portal/Radix dialog, for the same reason as
 * ImageLinkDialog: the editor sits inside CSS-transformed containers and can run
 * in browser fullscreen, where portals to document.body are not painted.
 */

const RATIOS = [
    { id: 'free', label: 'Free' },
    { id: 'original', label: 'Original' },
    { id: 'frame', label: 'Frame' },
    { id: '1:1', label: '1:1', value: 1 },
    { id: '4:5', label: '4:5', value: 4 / 5 },
    { id: '5:4', label: '5:4', value: 5 / 4 },
    { id: '3:4', label: '3:4', value: 3 / 4 },
    { id: '4:3', label: '4:3', value: 4 / 3 },
    { id: '2:3', label: '2:3', value: 2 / 3 },
    { id: '3:2', label: '3:2', value: 3 / 2 },
    { id: '9:16', label: '9:16', value: 9 / 16 },
    { id: '16:9', label: '16:9', value: 16 / 9 },
];

/**
 * A centred selection: the whole picture for a free crop, otherwise the biggest
 * box of that ratio that FITS — fitting both ways matters, since a portrait ratio
 * on a landscape picture (and the reverse) would otherwise run off the edge.
 */
function seedCrop(aspect, width, height) {
    if (!aspect || !(width > 0) || !(height > 0)) {
        return { unit: '%', x: 0, y: 0, width: 100, height: 100 };
    }
    let w = width;
    let h = width / aspect;
    if (h > height) {
        h = height;
        w = height * aspect;
    }
    return {
        unit: '%',
        x: ((width - w) / 2 / width) * 100,
        y: ((height - h) / 2 / height) * 100,
        width: (w / width) * 100,
        height: (h / height) * 100,
    };
}

/**
 * Turn the picture by whole quarters, so the preview and the export agree.
 *
 * `loadImage` rather than a bare `new Image()`: a saved thumbnail is a remote
 * R2 URL, and reading a canvas that a non-CORS image was drawn onto throws
 * ("Tainted canvases may not be exported").
 */
async function rotatedDataUrl(src, rot) {
    if (!rot) return src;
    try {
        const img = await loadImage(src);
        const swapped = rot === 90 || rot === 270;
        const w = swapped ? img.naturalHeight : img.naturalWidth;
        const h = swapped ? img.naturalWidth : img.naturalHeight;
        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        const ctx = c.getContext('2d');
        ctx.translate(w / 2, h / 2);
        ctx.rotate((rot * Math.PI) / 180);
        ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
        return c.toDataURL('image/jpeg', 0.92);
    } catch {
        return src; // un-rotated is better than a blank dialog
    }
}

export default function ThumbnailCropDialog({
    open,
    src,
    // Reopening the dialog resumes where the last crop left off.
    initialCrop = null,
    initialRatioId = 'free',
    initialCustom = null,
    initialRotation = 0,
    // The selected artboard, so "Frame" can offer the design's own proportions.
    frame = null,
    busy = false,
    onClose,
    onApply,
}) {
    const imgRef = useRef(null);
    // Which preview already got a starting selection — re-seeding would throw
    // away the admin's own rectangle.
    const seededRef = useRef('');
    const [crop, setCrop] = useState(null);
    const [ratioId, setRatioId] = useState(initialRatioId);
    const [customW, setCustomW] = useState(initialCustom?.w ?? '');
    const [customH, setCustomH] = useState(initialCustom?.h ?? '');
    const [rotation, setRotation] = useState(initialRotation);
    const [viewSrc, setViewSrc] = useState('');
    // Pixel size of the picture as shown (already rotated) — the crop rect's frame.
    const [dims, setDims] = useState(null);

    // Opening is the only thing that resets the dialog; edits inside it survive re-renders.
    useEffect(() => {
        if (!open) return;
        seededRef.current = '';
        setCrop(initialCrop || null);
        setRatioId(initialRatioId);
        setCustomW(initialCustom?.w ?? '');
        setCustomH(initialCustom?.h ?? '');
        setRotation(initialRotation);
        setDims(null);
        setViewSrc('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, src]);

    // Bake the current rotation into the picture the crop box sits on.
    useEffect(() => {
        if (!open || !src) return undefined;
        let cancelled = false;
        (async () => {
            const next = await rotatedDataUrl(src, rotation);
            if (!cancelled) setViewSrc(next);
        })();
        return () => { cancelled = true; };
    }, [open, src, rotation]);

    const frameAspect = frame && Number(frame.width) > 0 && Number(frame.height) > 0
        ? Number(frame.width) / Number(frame.height)
        : null;
    const originalAspect = dims ? dims.w / dims.h : null;

    const customAspect = (() => {
        const w = Number(customW);
        const h = Number(customH);
        return w > 0 && h > 0 ? w / h : null;
    })();

    const aspectFor = (id) => {
        if (id === 'free') return null;
        if (id === 'custom') return customAspect;
        if (id === 'original') return originalAspect;
        if (id === 'frame') return frameAspect;
        return RATIOS.find((r) => r.id === id)?.value ?? null;
    };
    const aspect = aspectFor(ratioId);

    const applyAspect = (nextId) => {
        setRatioId(nextId);
        if (!dims) return;
        setCrop(seedCrop(aspectFor(nextId), dims.w, dims.h));
    };

    const onImageReady = (node) => {
        const w = node?.naturalWidth;
        const h = node?.naturalHeight;
        if (!w || !h || !viewSrc) return;
        if (seededRef.current === viewSrc) return;
        const first = seededRef.current === '';
        seededRef.current = viewSrc;
        setDims({ w, h });
        // A resumed crop is only valid for the picture it was made on: keep it on
        // the first paint, re-seed after every rotation.
        if (first && initialCrop) return;
        setCrop(seedCrop(ratioId === 'original' ? w / h : aspect, w, h));
    };

    /**
     * An image restored from cache can be `complete` before `onLoad` is attached,
     * which would leave the dialog with no selection. Checking it from an effect
     * keyed on the picture — rather than from the `<img>` ref callback — is what
     * keeps this a one-shot: an inline ref callback is a new function on every
     * render, so React re-attaches it, and the setState inside would loop.
     */
    useEffect(() => {
        if (!viewSrc) return;
        const node = imgRef.current;
        if (node?.complete && node.naturalWidth) onImageReady(node);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewSrc]);

    const rotate = (delta) => {
        setRotation((r) => ((r + delta) % 360 + 360) % 360);
    };

    const reset = () => {
        setRatioId('free');
        setCustomW('');
        setCustomH('');
        setRotation(0);
        if (dims) setCrop(seedCrop(null, dims.w, dims.h));
    };

    if (!open || !src) return null;

    const outSize = dims && crop?.width && crop?.height
        ? {
            w: Math.max(1, Math.round((crop.width / 100) * dims.w)),
            h: Math.max(1, Math.round((crop.height / 100) * dims.h)),
        }
        : null;

    const chipCls = (active) => `h-9 px-3 rounded-[6px] text-[12px] font-medium border transition-colors
        ${active
        ? 'border-[#0d99ff] bg-[#0d99ff] text-white'
        : `${T.border} ${T.text} ${T.hoverBg}`}
        disabled:opacity-30 disabled:pointer-events-none`;

    return (
        <div className="fixed inset-0 z-[300] bg-black/50">
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Crop thumbnail"
                onKeyDown={(e) => {
                    // The editor listens for single-key shortcuts on the window.
                    e.stopPropagation();
                    if (e.key === 'Escape' && !busy) onClose?.();
                }}
                // Full screen: the crop box is the work, and a small dialog made a
                // large picture too small to place a rectangle on accurately.
                className={`absolute inset-0 flex flex-col ${T.panel} ${T.text}`}
            >
                <div className={`px-4 pt-4 pb-3 border-b ${T.border} flex items-start gap-3 shrink-0`}>
                    <div className="min-w-0">
                        <div className="text-[13px] font-semibold">Crop thumbnail</div>
                        <p className={`text-[11px] leading-snug mt-0.5 ${T.textSoft}`}>
                            Pick a ratio or drag the corners. Gallery cards show whatever shape you
                            crop to, and the picture you started from is kept so you can re-crop any time.
                        </p>
                    </div>
                    <button
                        type="button"
                        disabled={busy}
                        aria-label="Close"
                        onClick={() => onClose?.()}
                        className={`ml-auto h-9 w-9 shrink-0 flex items-center justify-center rounded-[6px]
                            border ${T.border} ${T.hoverBg} disabled:opacity-40`}
                    >
                        <IconX size={16} />
                    </button>
                </div>

                <div className="px-4 py-3 flex-1 min-h-0 flex flex-col">
                    {/* One toolbar: what the crop WILL be on the left, what to do to
                        the picture on the right. */}
                    <div className="flex flex-wrap items-center gap-1.5 mb-3 shrink-0">
                        {RATIOS.map((r) => (
                            <button
                                key={r.id}
                                type="button"
                                disabled={(r.id === 'frame' && !frameAspect) || (r.id === 'original' && !originalAspect)}
                                title={r.id === 'frame' && frame ? `Match ${frame.name}` : undefined}
                                onClick={() => applyAspect(r.id)}
                                className={chipCls(ratioId === r.id)}
                            >
                                {r.label}
                            </button>
                        ))}
                        <span className={`ml-1 text-[11px] ${T.textSoft}`}>Custom</span>
                        <input
                            value={customW}
                            inputMode="decimal"
                            placeholder="W"
                            onChange={(e) => {
                                const v = e.target.value.replace(/[^\d.]/g, '');
                                setCustomW(v);
                                setRatioId('custom');
                                if (dims && Number(v) > 0 && Number(customH) > 0) {
                                    setCrop(seedCrop(Number(v) / Number(customH), dims.w, dims.h));
                                }
                            }}
                            className={`w-14 h-9 px-2 text-[12px] text-center rounded-[6px] outline-none
                                border ${T.border} ${T.inputBg} ${T.text} focus:ring-1 focus:ring-[#0d99ff]`}
                        />
                        <span className={`text-[12px] ${T.textFaint}`}>:</span>
                        <input
                            value={customH}
                            inputMode="decimal"
                            placeholder="H"
                            onChange={(e) => {
                                const v = e.target.value.replace(/[^\d.]/g, '');
                                setCustomH(v);
                                setRatioId('custom');
                                if (dims && Number(v) > 0 && Number(customW) > 0) {
                                    setCrop(seedCrop(Number(customW) / Number(v), dims.w, dims.h));
                                }
                            }}
                            className={`w-14 h-9 px-2 text-[12px] text-center rounded-[6px] outline-none
                                border ${T.border} ${T.inputBg} ${T.text} focus:ring-1 focus:ring-[#0d99ff]`}
                        />

                        <div className="ml-auto flex items-center gap-1.5">
                            <span className={`mr-1 text-[11px] tabular-nums ${T.textSoft}`}>
                                {outSize ? `${outSize.w} × ${outSize.h} px` : 'Loading image…'}
                            </span>
                            <button
                                type="button"
                                title="Rotate left"
                                aria-label="Rotate left"
                                onClick={() => rotate(-90)}
                                className={`h-9 w-9 flex items-center justify-center rounded-[6px] border ${T.border} ${T.hoverBg}`}
                            >
                                <IconRotate2 size={16} />
                            </button>
                            <button
                                type="button"
                                title="Rotate right"
                                aria-label="Rotate right"
                                onClick={() => rotate(90)}
                                className={`h-9 w-9 flex items-center justify-center rounded-[6px] border ${T.border} ${T.hoverBg}`}
                            >
                                <IconRotateClockwise size={16} />
                            </button>
                            <button
                                type="button"
                                title="Reset crop"
                                onClick={reset}
                                className={`h-9 px-3 flex items-center gap-1.5 rounded-[6px] border ${T.border} ${T.hoverBg} text-[12px] font-medium`}
                            >
                                <IconRestore size={16} />
                                Reset
                            </button>
                        </div>
                    </div>

                    {/* The picture takes every pixel the chrome doesn't: a crop
                        rectangle is only as accurate as the image is big. */}
                    <div className="flex-1 min-h-0 flex items-center justify-center overflow-hidden
                        rounded-[6px] bg-[#f5f5f5] dark:bg-[#1e1e1e] p-2">
                        {viewSrc ? (
                            <ReactCrop
                                crop={crop || undefined}
                                onChange={(_, percentCrop) => setCrop(percentCrop)}
                                aspect={aspect || undefined}
                                keepSelection
                                ruleOfThirds
                                className="max-w-full max-h-full"
                            >
                                <img
                                    ref={imgRef}
                                    src={viewSrc}
                                    alt=""
                                    draggable={false}
                                    onLoad={(e) => onImageReady(e.currentTarget)}
                                    // ReactCrop measures the rendered <img>, so the cap
                                    // has to live here rather than on a wrapper.
                                    style={{ maxHeight: 'calc(100vh - 230px)' }}
                                    className="max-w-full object-contain"
                                />
                            </ReactCrop>
                        ) : (
                            <IconLoader2 size={18} className={`animate-spin ${T.textFaint}`} />
                        )}
                    </div>

                </div>

                <div className={`px-4 py-3 border-t ${T.border} flex items-center justify-end gap-2 shrink-0`}>
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => onClose?.()}
                        className={`h-9 px-4 rounded-[6px] text-[12px] font-medium border ${T.border} ${T.hoverBg} disabled:opacity-40`}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={busy || !crop?.width || !crop?.height}
                        onClick={() => onApply?.({
                            crop,
                            rotation,
                            ratioId,
                            custom: { w: customW, h: customH },
                        })}
                        className="h-9 px-4 rounded-[6px] text-[12px] font-semibold bg-[#0d99ff] text-white
                            hover:bg-[#0b87e0] disabled:opacity-40 flex items-center gap-1.5"
                    >
                        {busy && <IconLoader2 size={14} className="animate-spin" />}
                        {busy ? 'Applying…' : 'Apply crop'}
                    </button>
                </div>
            </div>
        </div>
    );
}
